// views/Tarefas/SolicitacaoModalGlobal.tsx
//
// Modal de Solicitação que abre por cima de QUALQUER tela, sem navegar — escuta
// 'vigorhub:open-solicitacao' (disparado por src/lib/abrirSolicitacao.ts) e busca tudo sozinho
// via useSolicitacaoActions. Montado uma vez em App.tsx > MainLayout, igual TermoReprovadoPopup /
// InadimplenciaDashboard.
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { api } from '../../src/services/api';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import type { Profile, Projeto, TarefaTimeEntry, TarefaStatusEvent, Tarefa } from '../../types';
import { ABRIR_SOLICITACAO_EVENTO } from '../../src/lib/abrirSolicitacao';
import type { AbrirSolicitacaoDetail } from '../../src/lib/abrirSolicitacao';
import { useSolicitacaoActions } from './useSolicitacaoActions';
import { permissoesSolicitacao } from './permissoesSolicitacao';
import SolicitacaoDetailModal from './SolicitacaoDetailModal';
import DirecionarSolicitacaoModal from './DirecionarSolicitacaoModal';
import ConfirmationModal from '../../src/components/ConfirmationModal';

const SolicitacaoModalGlobal: React.FC = () => {
  const { user, profile } = useAuth();
  const { addToast } = useToast();

  const [taskId, setTaskId] = useState<string | null>(null);
  const [users, setUsers] = useState<Profile[]>([]);
  const [projetos, setProjetos] = useState<Projeto[]>([]);

  // Cronômetro — cópia própria e independente da de TarefasView.tsx (ver comentário em
  // useSolicitacaoActions.ts sobre por que não é compartilhada).
  const [openTimeEntry, setOpenTimeEntry] = useState<TarefaTimeEntry | null>(null);
  const [timerBusy, setTimerBusy] = useState(false);
  const [timerTick, setTimerTick] = useState(0);
  const [taskTimeEntries, setTaskTimeEntries] = useState<TarefaTimeEntry[]>([]);
  const [activityEvents, setActivityEvents] = useState<TarefaStatusEvent[]>([]);

  const actions = useSolicitacaoActions(taskId);
  const { task } = actions;

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<AbrirSolicitacaoDetail>).detail;
      if (detail?.id) setTaskId(detail.id);
    };
    window.addEventListener(ABRIR_SOLICITACAO_EVENTO, handler);
    return () => window.removeEventListener(ABRIR_SOLICITACAO_EVENTO, handler);
  }, []);

  // Avisa quando a busca terminou e não achou nada (RLS negou ou a tarefa não existe mais).
  useEffect(() => {
    if (actions.notFound) {
      addToast('Não foi possível abrir essa solicitação.', 'error');
      setTaskId(null);
    }
  }, [actions.notFound]);

  // Perfis e projetos só precisam vir uma vez — reusa se já buscou numa abertura anterior.
  useEffect(() => {
    if (!taskId || users.length > 0) return;
    api.auth.listProfiles().then(setUsers);
  }, [taskId, users.length]);

  useEffect(() => {
    if (!taskId || projetos.length > 0) return;
    supabase.from('projetos').select('*').then(({ data }) => { if (data) setProjetos(data as Projeto[]); });
  }, [taskId, projetos.length]);

  useEffect(() => {
    if (!user) return;
    supabase.from('tarefa_time_entries').select('*').eq('user_id', user.id).is('ended_at', null).maybeSingle()
      .then(({ data }) => setOpenTimeEntry(data as any));
  }, [user]);

  useEffect(() => {
    if (!openTimeEntry) return;
    const interval = setInterval(() => setTimerTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, [openTimeEntry]);

  useEffect(() => {
    if (!taskId) {
      setTaskTimeEntries([]);
      setActivityEvents([]);
      return;
    }
    (async () => {
      const [{ data: entries }, { data: eventos }] = await Promise.all([
        supabase.from('tarefa_time_entries').select('*, user:profiles(*)').eq('tarefa_id', taskId).order('started_at', { ascending: false }),
        supabase.from('tarefa_status_events').select('*, user:profiles(*)').eq('tarefa_id', taskId).order('created_at', { ascending: true }),
      ]);
      setTaskTimeEntries((entries || []) as any);
      setActivityEvents((eventos || []) as any);
    })();
  }, [taskId]);

  const closeOpenTimeEntry = async () => {
    if (!openTimeEntry) return;
    const endedAt = new Date();
    const durationSeconds = Math.max(0, Math.round((endedAt.getTime() - new Date(openTimeEntry.started_at).getTime()) / 1000));
    const { error } = await supabase.from('tarefa_time_entries').update({ ended_at: endedAt.toISOString(), duration_seconds: durationSeconds }).eq('id', openTimeEntry.id);
    if (error) throw error;
    const closedEntry = { ...openTimeEntry, ended_at: endedAt.toISOString(), duration_seconds: durationSeconds };
    setTaskTimeEntries(prev => prev.map(entry => entry.id === closedEntry.id ? { ...entry, ...closedEntry } : entry));
    setOpenTimeEntry(null);
  };

  const onStartTimer = async () => {
    if (!user || !task) return;
    if (openTimeEntry) {
      addToast('Você já tem um cronômetro rodando em outra tarefa. Pause ou finalize antes de iniciar outro.', 'error');
      return;
    }
    setTimerBusy(true);
    try {
      const { data, error } = await supabase.from('tarefa_time_entries').insert({ tarefa_id: task.id, user_id: user.id }).select().single();
      if (error) throw error;
      setOpenTimeEntry(data as any);
      setTaskTimeEntries(prev => [data as any, ...prev]);
      const status = task.solicitacao_status || 'novo';
      if (status === 'novo' || status === 'em_analise') actions.onStatusChange('em_desenvolvimento');
      addToast('Cronômetro iniciado.', 'success');
    } catch (error: any) {
      addToast('Erro ao iniciar cronômetro: ' + (error?.message || JSON.stringify(error)), 'error');
    } finally {
      setTimerBusy(false);
    }
  };

  const onPauseTimer = async () => {
    setTimerBusy(true);
    try {
      await closeOpenTimeEntry();
      addToast('Cronômetro pausado.', 'success');
    } catch (error: any) {
      addToast('Erro ao pausar cronômetro: ' + (error?.message || JSON.stringify(error)), 'error');
    } finally {
      setTimerBusy(false);
    }
  };

  const onFinishTimer = async () => {
    setTimerBusy(true);
    try {
      await closeOpenTimeEntry();
      addToast('Cronômetro finalizado. Escolha o próximo status da solicitação.', 'success');
    } catch (error: any) {
      addToast('Erro ao finalizar cronômetro: ' + (error?.message || JSON.stringify(error)), 'error');
    } finally {
      setTimerBusy(false);
    }
  };

  const onAddManualTime = async (minutes: number, date: string) => {
    if (!user || !task || minutes <= 0) return;
    try {
      const reference = new Date(`${date}T12:00:00`).toISOString();
      const durationSeconds = Math.round(minutes * 60);
      const { data, error } = await supabase.from('tarefa_time_entries')
        .insert({ tarefa_id: task.id, user_id: user.id, started_at: reference, ended_at: reference, duration_seconds: durationSeconds, is_manual: true })
        .select('*, user:profiles(*)').single();
      if (error) throw error;
      setTaskTimeEntries(prev => [data as any, ...prev].sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime()));
      addToast('Tempo adicionado.', 'success');
    } catch (error: any) {
      addToast('Erro ao adicionar tempo: ' + (error?.message || JSON.stringify(error)), 'error');
      throw error;
    }
  };

  const onEditTimeEntry = async (entryId: number, minutes: number) => {
    if (minutes <= 0) return;
    try {
      const durationSeconds = Math.round(minutes * 60);
      const { error } = await supabase.from('tarefa_time_entries').update({ duration_seconds: durationSeconds }).eq('id', entryId);
      if (error) throw error;
      setTaskTimeEntries(prev => prev.map(e => e.id === entryId ? { ...e, duration_seconds: durationSeconds } : e));
      addToast('Tempo atualizado.', 'success');
    } catch (error: any) {
      addToast('Erro ao editar tempo: ' + (error?.message || JSON.stringify(error)), 'error');
      throw error;
    }
  };

  const timerElapsedSeconds = (() => {
    if (!openTimeEntry) return 0;
    void timerTick;
    return Math.max(0, Math.round((Date.now() - new Date(openTimeEntry.started_at).getTime()) / 1000));
  })();

  const close = () => setTaskId(null);

  if (taskId && actions.loading) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <Loader2 className="animate-spin text-white" size={32} />
      </div>
    );
  }

  if (!taskId || !task) return null;

  const perms = permissoesSolicitacao(task, profile, user?.id);
  const allTarefas: Tarefa[] = [task, ...actions.subtarefas];

  return (
    <>
      <SolicitacaoDetailModal
        task={task}
        allTarefas={allTarefas}
        users={users}
        currentUserId={user?.id}
        isCreatorOrAdmin={perms.isCreatorOrAdmin}
        canDirecionarSolicitacoes={perms.canDirecionarSolicitacoes}
        canSelfAssignSolicitacao={perms.canSelfAssignSolicitacao}
        comments={actions.comments}
        taskTimeEntries={taskTimeEntries}
        activityEvents={activityEvents}
        openTimeEntry={openTimeEntry}
        timerBusy={timerBusy}
        timerElapsedSeconds={timerElapsedSeconds}
        onClose={close}
        onOpenSubtask={actions.onOpenSubtask}
        onStatusChange={actions.onStatusChange}
        onTipoChange={actions.onTipoChange}
        onTipoOutroDetalheChange={actions.onTipoOutroDetalheChange}
        onDepartamentoChange={actions.onDepartamentoChange}
        onBloqueadaPorPessoaChange={actions.onBloqueadaPorPessoaChange}
        onBloqueadaPorTextoChange={actions.onBloqueadaPorTextoChange}
        onDefinirDependencia={actions.onDefinirDependencia}
        onSolicitanteChange={actions.onSolicitanteChange}
        projetos={projetos}
        podeVincularProjeto={perms.podeVincularProjeto}
        onProjetoChange={actions.onProjetoChange}
        onSaveTitle={actions.onSaveTitle}
        onSaveDescription={actions.onSaveDescription}
        onSelfAssign={actions.onSelfAssign}
        onDirecionar={actions.onDirecionar}
        onReagendar={actions.onReagendar}
        onRequestDelete={actions.onRequestDelete}
        onStartTimer={onStartTimer}
        onPauseTimer={onPauseTimer}
        onFinishTimer={onFinishTimer}
        onAddManualTime={onAddManualTime}
        onEditTimeEntry={onEditTimeEntry}
        onAddAttachment={actions.onAddAttachment}
        onRemoveAttachment={actions.onRemoveAttachment}
        onAddSubtask={actions.onAddSubtask}
        onSendComment={actions.onSendComment}
        onEditComment={actions.onEditComment}
        onDeleteComment={actions.onDeleteComment}
      />
      <DirecionarSolicitacaoModal
        isOpen={actions.direcionando}
        task={task}
        users={users}
        onClose={actions.cancelDirecionar}
        onSubmit={actions.confirmDirecionar}
      />
      <ConfirmationModal
        isOpen={actions.requestingDelete}
        onClose={actions.cancelDelete}
        onConfirm={async () => { const excluiu = await actions.confirmDelete(); if (excluiu) close(); }}
        title="Excluir Tarefa"
        description="Tem certeza que deseja excluir esta tarefa?"
        confirmText="Excluir"
        variant="danger"
        isLoading={actions.deleting}
      />
    </>
  );
};

export default SolicitacaoModalGlobal;
